/** @format */

import { AbstractControl, FormGroup } from "@angular/forms";
import { parseZone, tz } from "moment-timezone";
import { getAdditionalBookings, parkingTypes } from "../../../functions/booking-utils.functions";
import { NsEmpWebExpData } from "../../../models/emp-web-exp.model";

const Minute = 60 * 1000;
const parkingTypeMap = {
  "TWO-WHEELER-PARKING": "PARKING_TWO",
  "FOUR-WHEELER-PARKING": "PARKING_FOUR",
  PARKING: "PARKING_FOUR"
};

function createRecurrenceBookingObject(
  req: NsEmpWebExpData.IBookingRequest,
  rruleObj: NsEmpWebExpData.RRULE,
  commute: AbstractControl,
  profile: NsEmpWebExpData.IEmployee,
  office,
  channel?: string
): NsEmpWebExpData.IBookingRequestObject {
  let commuteObj = [];
  if (commute?.value.login?.id === "transport") {
    commuteObj.push(appendTransport(req, "LOGIN", "SCHEDULED", profile, office, null, null, true));
  }
  if (commute?.value.logout?.id === "transport") {
    commuteObj.push(appendTransport(req, "LOGOUT", "SCHEDULED", profile, office, null, null, true));
  }
  let obj = {
    updatePreferences: false,
    channel: channel,
    bookings: [req],
    schedules: commuteObj,
    rrule: rruleObj
  };
  return obj;
}
function createRruleObject(recurrence): NsEmpWebExpData.RRULE {
  const { days, repeatedDay } = recurrence.repeatsEvery;
  const obj = {
    days: days,
    repeatedDay: repeatedDay
  };
  return obj;
}

export const createBookingRequest = (
  profile: NsEmpWebExpData.IEmployee,
  form: FormGroup,
  notAllowedDates: string[] = [],
  format: string,
  recurrenceEnabled: boolean,
  allowWeeklyOffBookings: boolean,
  parkingDisclaimerAck: boolean,
  channel?: string
): NsEmpWebExpData.IBookingRequest[] | NsEmpWebExpData.IBookingRequestObject => {
  const { startDate, endDate, premiseId, bookingRequestType, startShift, endShift, office, reason, recurrence, inputText } = form.value;
  const list = [];
  let requestObjRecurrence;
  const date = tz(startDate, format, office.timezone).startOf("day");
  const recurrenceEndDate = recurrence.recurrenceVisibility ? tz(recurrence.endDate, format, office.timezone).startOf("day") : undefined;
  const dateEnd = recurrenceEndDate
    ? tz(recurrenceEndDate || startDate, format, office.timezone).endOf("day")
    : tz(endDate || startDate, format, office.timezone).endOf("day");
  while (date.isSameOrBefore(dateEnd)) {
    if (
      !allowWeeklyOffBookings &&
      (profile?.empWeeklyOff?.includes(date.day() + 1) || notAllowedDates.some(d => tz(d, format, office.timezone).isSame(date, "day")))
    ) {
      date.add(1, "day");
      continue;
    }
    const start = date.valueOf() + startShift.timeInMinutes * Minute;
    const end = recurrenceEndDate ? recurrenceEndDate.valueOf() + endShift.timeInMinutes * Minute : date.valueOf() + endShift.timeInMinutes * Minute;
    let req = genBookingObj(
      profile,
      start,
      end,
      startShift.shiftId,
      endShift.shiftId,
      undefined,
      "CREATE",
      bookingRequestType,
      office,
      premiseId,
      undefined,
      reason == "OTHERS" ? inputText : reason,
      endShift?.isNextDay,
      startShift.isAdhoc || endShift.isAdhoc,
      channel,
      form
    );
    const rruleObj = recurrenceEnabled && createRruleObject(recurrence);
    const seat = form.get("seat");
    updateSeat(req, seat?.value);
    const meal = form.get("meal");
    if (meal.value) {
      req = appendMeal(req, meal.value, null, channel);
    }
    const commute = form.get("commute");
    if (commute?.value.login?.id === "parking" || commute?.value.login?.id === "parkingWaitlist") {
      req = appendParking(
        req,
        "CREATE",
        commute?.value.login.value,
        undefined,
        commute?.value.registrationNumber,
        channel,
        commute?.value.login?.id === "parkingWaitlist" ? commute?.value.login?.value : [],
        parkingDisclaimerAck,
        commute?.value.entityTypeId,
        commute?.value.vehicleId
      );
    }
    if (recurrenceEnabled && recurrence.recurrenceVisibility) {
      requestObjRecurrence = createRecurrenceBookingObject(req, rruleObj, commute, profile, office, channel);
    } else {
      if (commute?.value.login?.id === "transport") {
        req = appendTransport(req, "LOGIN", "SCHEDULED", profile, office, null, null, false);
      }
      if (commute?.value.logout?.id === "transport") {
        req = appendTransport(req, "LOGOUT", "SCHEDULED", profile, office, null, null, false);
      }
    }
    recurrenceEnabled && recurrence.recurrenceVisibility ? list.push(requestObjRecurrence) : list.push(req);
    date.add(1, "day");
  }
  if (recurrenceEnabled && recurrence.recurrenceVisibility) {
    return list[0];
  }
  return list;
};

export const editBookingRequest = (
  profile: NsEmpWebExpData.IEmployee,
  form: FormGroup,
  booking: NsEmpWebExpData.IBookingDetailDTOS,
  channel?: string,
  parkingDisclaimer?: boolean
): NsEmpWebExpData.IBookingRequest[] => {
  const { premiseId, bookingRequestType, startShift, endShift, office, reason, inputText } = form.value;
  const list = [];
  const date = parseZone(booking.startTime).tz(office.timezone).startOf("day");
  // const dateEnd = parseZone(booking.startTime).tz(office.timezone).endOf("day");
  const start = form.get("startShift").disabled ? booking.startTime : date.valueOf() + startShift.timeInMinutes * Minute;
  const end = form.get("endShift").disabled ? booking.endTime : date.valueOf() + endShift.timeInMinutes * Minute;
  let req = genBookingObj(
    profile,
    start,
    end,
    startShift?.shiftId || booking.loginShiftId,
    endShift?.shiftId || booking.logoutShiftId,
    booking.bookingRequestId,
    "UPDATE",
    bookingRequestType,
    office,
    premiseId,
    booking,
    reason == "OTHERS" ? inputText : reason,
    endShift?.isNextDay,
    startShift?.isAdhoc || endShift?.isAdhoc,
    channel,
    form
  );
  const seat = form.get("seat");
  updateSeat(req, seat.value, booking);
  const meal = form.get("meal");
  if (req.bookingRequestType === "OFFICE") {
    appendMeal(req, meal.value, booking, channel, "UPDATE");
  }
  const commute = form.get("commute");
  updateCommute(
    req,
    profile,
    commute,
    office,
    booking,
    commute.value.registrationNumber,
    channel,
    parkingDisclaimer,
    commute.value.entityTypeId,
    commute.value.vehicleId
  );
  list.push(req);
  return list;
};

export const cancelBookingRequest = (
  profile: NsEmpWebExpData.IEmployee,
  booking: NsEmpWebExpData.IBookingDetailDTOS,
  offices: NsEmpWebExpData.IOffice[],
  cancelBooking = true,
  cancelLoginTransport = true,
  cancelLogoutTransport = true
): NsEmpWebExpData.IBookingRequest[] => {
  const ofc = offices.find(ofc => ofc.guid === booking.officeId);
  const list = [];
  let req = genBookingObj(
    profile,
    booking.startTime,
    booking.endTime,
    booking.loginShiftId,
    booking.logoutShiftId,
    booking.bookingRequestId,
    cancelBooking ? "CANCEL" : "UPDATE",
    booking.bookingRequestType,
    ofc,
    booking.premiseId,
    booking,
    booking.bookingReason
  );
  if (cancelLoginTransport && booking.extras?.login) {
    req = appendTransport(req, "LOGIN", "SCHEDULE_CANCELLED", profile, ofc, booking.extras?.loginShiftId);
  }
  if (cancelLogoutTransport && booking.extras?.logout) {
    req = appendTransport(req, "LOGOUT", "SCHEDULE_CANCELLED", profile, ofc, booking.extras?.logoutShiftId);
  }
  list.push(req);
  return list;
};

export const cancelBooking = (
  booking: NsEmpWebExpData.IBookingDetailDTOS,
  recurrenceId?: string,
  type?: string[],
  channel?: string
): NsEmpWebExpData.IBookingCancelRequest[] => {
  const list: NsEmpWebExpData.IBookingCancelRequest[] = [];
  list.push({
    bookingType: type?.length > 0 ? type : [booking?.premiseType],
    recurrenceId: recurrenceId,
    requestId: booking?.bookingRequestId,
    channel: channel
  });
  return list;
};

function updateSeat(
  req: NsEmpWebExpData.IBookingRequest,
  seat: NsEmpWebExpData.ISeatData,
  booking?: NsEmpWebExpData.IBookingDetailDTOS
): NsEmpWebExpData.IBookingRequest {
  if (seat?.seatId && seat?.floorId && seat?.seatName) {
    req.usersBooking[0].premiseId = seat.floorId;
    req.usersBooking[0].premiseName = seat.floorName;
    req.usersBooking[0].tokenId = seat.seatId;
    req.usersBooking[0].tokenName = seat.seatName;
  } else if (booking?.tokenId && !seat) {
    req.usersBooking[0].tokenId = "";
    req.usersBooking[0].tokenName = "";
  }
  return req;
}

function updateCommute(
  req: NsEmpWebExpData.IBookingRequest,
  profile: NsEmpWebExpData.IEmployee,
  commute: AbstractControl,
  ofc: NsEmpWebExpData.IOffice,
  booking: NsEmpWebExpData.IBookingDetailDTOS,
  regNo: string,
  channel?: string,
  parkingDisclaimer?: boolean,
  entityTypeId?: string,
  vehicleId?: string
): NsEmpWebExpData.IBookingRequest {
  const currentParking = getAdditionalBookings(booking, parkingTypes)?.[0];
  const currentParkingType =
    (currentParking?.typeWiseWaitingList && Object.keys(currentParking?.typeWiseWaitingList))?.length > 0 ? "parkingWaitlist" : "parking";
  let requestType: "CREATE" | "UPDATE" | "CANCEL" = "UPDATE";
  if (currentParkingType === "parkingWaitlist" && currentParkingType !== commute?.value.login?.id) requestType = "CREATE";
  else if (commute?.value.login?.id === "parkingWaitlist" && commute?.value.login?.value?.length === 0) requestType = "CANCEL";
  if (commute?.value.login?.id === "parking" || commute?.value.login?.id === "parkingWaitlist") {
    req = appendParking(
      req,
      requestType,
      commute?.value.login.value,
      currentParkingType === commute?.value.login?.id ? currentParking : undefined,
      regNo,
      channel,
      commute?.value.login?.id === "parkingWaitlist" ? commute?.value.login?.value : [],
      parkingDisclaimer,
      entityTypeId,
      vehicleId
    );
  }
  if (commute?.value.login?.id === "transport" || commute?.value.logout?.id === "transport") {
    if (commute?.value.login?.id === "transport") {
      req = appendTransport(req, "LOGIN", "SCHEDULED", profile, ofc, booking.extras?.loginShiftId);
    }
    if (commute?.value.logout?.id === "transport") {
      req = appendTransport(req, "LOGOUT", "SCHEDULED", profile, ofc, booking.extras?.logoutShiftId);
    }
  }
  const cancelLoginCab = booking.extras?.login && (!commute?.value.login || commute?.value.login?.id !== "transport");
  const cancelLogoutCab = booking.extras?.logout && (!commute?.value.logout || commute?.value.logout?.id !== "transport");
  if (cancelLoginCab || cancelLogoutCab) {
    if (cancelLoginCab) {
      const login = getTransport(booking, "LOGIN");
      req = appendTransport(req, "LOGIN", "SCHEDULE_CANCELLED", profile, ofc, booking.extras?.loginShiftId, login);
    }
    if (cancelLogoutCab) {
      const logout = getTransport(booking, "LOGOUT");
      req = appendTransport(req, "LOGOUT", "SCHEDULE_CANCELLED", profile, ofc, booking.extras?.logoutShiftId, logout);
    }
  }
  const parking = getAdditionalBookings(booking, parkingTypes)?.[0];
  if (
    parking &&
    ((parking?.typeWiseWaitingList !== null && !Object.keys(parking?.typeWiseWaitingList)?.length) || parking?.typeWiseWaitingList == null) &&
    (!commute?.value.login || commute?.value.login?.id !== "parking")
  ) {
    req = appendParking(req, "CANCEL", undefined, parking, null, channel);
  }
  if (
    parking &&
    parking?.typeWiseWaitingList !== null &&
    Object.keys(parking?.typeWiseWaitingList)?.length &&
    (!commute?.value.login || commute?.value.login?.id !== "parkingWaitlist")
  ) {
    req = appendParking(req, "CANCEL", undefined, parking, null, channel);
  }
  return req;
}

function genBookingObj(
  profile: NsEmpWebExpData.IEmployee,
  start: number,
  end: number,
  startShiftId: string,
  endShiftId: string,
  requestId: string | undefined,
  operation: "CREATE" | "UPDATE" | "CANCEL",
  type: NsEmpWebExpData.IBookingRequestType,
  office: NsEmpWebExpData.IOffice,
  premiseId: string,
  booking?: NsEmpWebExpData.IBookingDetailDTOS,
  reason?: string,
  isNextDay?: boolean,
  isAdhoc?: boolean,
  channel?: string,
  form?: FormGroup
): NsEmpWebExpData.IBookingRequest {
  let bookingStatus = operation === "CANCEL" ? "CANCELLED" : "CONFIRMED";
  if (operation === "UPDATE") {
    bookingStatus = booking?.requestStatus;
  }
  const obj = {
    empGuid: profile.empGuid, // pass
    endTime: end, // pass
    error: false,
    errorMessage: "", // empty
    requestId: requestId || "", // empty
    requestType: operation, //doubt
    startTime: start, // pass
    bookingRequestType: type,
    name: profile.name,
    timezone: office.timezone,
    usersBooking: [
      {
        bookingId: booking?.bookingId || "", //empty
        bookingStatus, // CONFIRMED
        bookingReason: reason || undefined,
        channel: channel, //pass
        endTime: end, //pass
        eventReason: "", //doubt
        extras: {
          isAdhoc: isAdhoc,
          isNextDayLogout: isNextDay,
          dynamicData: form?.value?.specialRequests,
        }, //doubt
        loginShiftId: startShiftId || "", //doubt //should come in getBookings
        logoutShiftId: endShiftId || "", //doubt // ---above --
        officeId: office.guid, //pass //getBookings
        premiseId: premiseId, // getBookings
        premiseName: type === "OFFICE" ? booking?.premiseName : undefined, // getBookings
        requestId: requestId || "", // empty
        requestType: operation, // PASS
        signinTime: 0, // doubt
        startTime: start, //pass
        type: type, //pass
        bookingRequestType: type,
        tokenId: type === "OFFICE" ? booking?.tokenId : undefined,
        tokenName: type === "OFFICE" ? booking?.tokenName : undefined // getBookings
      }
    ]
  };
  if(!!form?.value?.projectCode){
    obj.usersBooking[0].extras['projectCode'] = form?.value?.projectCode
  }
  return obj;
}

function appendParking(
  booking: NsEmpWebExpData.IBookingRequest,
  operation: "CREATE" | "UPDATE" | "CANCEL",
  parking?: NsEmpWebExpData.IParkingPremise,
  oldParking?: NsEmpWebExpData.IBookingDetailDTOS,
  registrationNumber?: string,
  channel?: string,
  waitingPremises?: NsEmpWebExpData.IParkingPremise[],
  disclaimerAck?: boolean,
  entityTypeId?: string,
  vehicleId?: string
): NsEmpWebExpData.IBookingRequest {
  const typeWaitlist = {};
  if (waitingPremises) {
    waitingPremises.map((item: any) => {
      if (item.hasOwnProperty("waitListType")) {
        if (item.waitListType === "CAR") {
          if (typeWaitlist.hasOwnProperty(item.premiseId)) {
            typeWaitlist[item.premiseId] = ["PARKING_FOUR", "PARKING_TWO"];
          } else {
            typeWaitlist[item.premiseId] = ["PARKING_FOUR"];
          }
        } else {
          if (typeWaitlist.hasOwnProperty(item.premiseId)) {
            typeWaitlist[item.premiseId] = ["PARKING_FOUR", "PARKING_TWO"];
          } else {
            typeWaitlist[item.premiseId] = ["PARKING_TWO"];
          }
        }
      } else {
        if (item.premiseType === "FOUR-WHEELER-PARKING") {
          typeWaitlist[item.premiseId] = ["PARKING_FOUR"];
        } else if (item.premiseType === "TWO-WHEELER-PARKING") {
          typeWaitlist[item.premiseId] = ["PARKING_TWO"];
        }
      }
    });
  }

  const parkingReq: NsEmpWebExpData.IUsersBookingEntity = {
    bookingId: oldParking?.bookingId || "", //empty
    bookingStatus: operation === "CANCEL" ? "CANCELLED" : "CONFIRMED", // CONFIRMED
    channel: channel, //pass
    endTime: booking.endTime, //pass
    eventReason: "", //doubt
    extras: {
      registrationNumber: registrationNumber ? registrationNumber : "",
      parkingDisclaimer: disclaimerAck
    }, //doubt
    loginShiftId: booking.usersBooking?.[0].loginShiftId || "", //doubt //should come in getBookings
    logoutShiftId: booking.usersBooking?.[0].logoutShiftId || "", //doubt // ---above --
    officeId: booking.usersBooking?.[0].officeId, //pass //getBookings
    premiseId: parking?.premiseId || oldParking?.premiseId || null, // getBookings
    premiseName: parking?.premise || oldParking?.premiseName || null,
    requestId: oldParking?.bookingRequestId || "", // empty
    requestType: operation === "UPDATE" && !oldParking ? "CREATE" : operation, // PASS
    signinTime: 0, // doubt
    startTime: booking.startTime, //pass
    type: parkingTypeMap[parking?.premiseType] || parking?.premiseType || oldParking?.premiseType || parkingTypeMap[waitingPremises[0]?.premiseType], //pass
    waitingPremiseIds: [],
    typeWiseWaitingList: typeWaitlist,
    bookingRequestType: "OFFICE",
    tokenId: parking?.seatId || null,
    tokenName: parking?.seatName || null
  };
  if (!!vehicleId) {
    parkingReq.extras["vehicleId"] = vehicleId;
    parkingReq.extras["entityTypeId"] = entityTypeId;
  }
  booking.usersBooking.push(parkingReq);
  return booking;
}

function getTransport(booking: NsEmpWebExpData.IBookingDetailDTOS, type: "LOGIN" | "LOGOUT"): NsEmpWebExpData.ITransportRequest | undefined {
  const transport = booking.userRequestDTOS?.find(b => b.direction === type && b.status !== "SCHEDULE_CANCELLED");
  return transport;
}

function appendTransport(
  booking: NsEmpWebExpData.IBookingRequest,
  direction: "LOGIN" | "LOGOUT",
  operation: "SCHEDULED" | "SCHEDULE_CANCELLED",
  profile: NsEmpWebExpData.IEmployee,
  office: NsEmpWebExpData.IOffice,
  transportId?: string,
  transport?: NsEmpWebExpData.ITransportRequest,
  recurrenceEnabled?: boolean
) {
  let scheduleObj;
  const key = direction === "LOGIN" ? "loginUserRequestDTO" : "logoutUserRequestDTO";
  const time = direction === "LOGIN" ? booking.startTime : booking.endTime;
  const homeLocation: NsEmpWebExpData.ITransportLocation = {
    id: profile.homeAddressInfo?.guid,
    latitude: Number(profile.homeAddressInfo?.geoCord?.split(",")?.[0] || "0"),
    longitude: Number(profile.homeAddressInfo?.geoCord?.split(",")?.[1] || "0"),
    address: profile.homeAddressInfo?.address
  };
  const officeLocation: NsEmpWebExpData.ITransportLocation = {
    id: office.guid,
    latitude: Number(office?.geoCord?.split(",")?.[0] || "0"),
    longitude: Number(office?.geoCord?.split(",")?.[1] || "0"),
    address: office.address
  };
  const tripTime =
    operation === "SCHEDULE_CANCELLED" && transport?.tripdetail?.tripTime
      ? transport.tripdetail.tripTime
      : parseZone(time).tz(office.timezone).format("DD/MM/YYYY HH:mm:ss");
  if (!recurrenceEnabled) {
    booking[key] = {
      direction,
      status: operation,
      tripdetail: {
        requestType: "PLANNED",
        tripTime,
        nextDayLogout: false,
        pickupLocation: direction === "LOGIN" ? homeLocation : officeLocation,
        dropLocation: direction === "LOGIN" ? officeLocation : homeLocation
      },
      bookingId: transportId ? booking?.usersBooking?.[0]?.bookingId : undefined,
      scheduleId: transportId
    };
    return booking;
  } else if (recurrenceEnabled) {
    scheduleObj = {
      direction,
      status: operation,
      tripdetail: {
        requestType: "PLANNED",
        tripTime,
        nextDayLogout: false,
        pickupLocation: direction === "LOGIN" ? homeLocation : officeLocation,
        dropLocation: direction === "LOGIN" ? officeLocation : homeLocation
      },
      markedAsLeave: false,
      editCutoff: 0,
      cancelCutoff: 0
    };
    return scheduleObj;
  }
  return booking;
}

function appendMeal(
  booking: NsEmpWebExpData.IBookingRequest,
  meal: NsEmpWebExpData.IMeal[],
  bookingRequest?: NsEmpWebExpData.IBookingDetailDTOS,
  channel?: string,
  mode?: string
): NsEmpWebExpData.IBookingRequest {
  const mealBooking = getAdditionalBookings(bookingRequest, ["MEALS"])?.[0];
  if (!mealBooking && (!meal || meal.length === 0)) {
    return booking;
  }
  const mealReq: NsEmpWebExpData.IUsersBookingEntity = {
    bookingId: mealBooking?.bookingId || "",
    bookingStatus: meal?.[0]?.mealData.length > 0 ? "CONFIRMED" : "CANCELLED",
    channel: channel,
    endTime: booking.endTime,
    eventReason: "",
    extras: meal
      ? {
          bookingId: bookingRequest?.additionalBookings?.[0]?.bookingId, //ID HERE
          mealData: meal[0]?.mealData || null,
          premiseId: meal[0]?.premiseId,
          premiseName: meal[0]?.premiseName || "",
          premiseEndTime: meal[0]?.premiseEndTime || undefined,
          premiseStartTime: meal[0]?.premiseStartTime || undefined
        }
      : {
          ...mealBooking?.extras
        },
    loginShiftId: booking.usersBooking?.[0].loginShiftId || "",
    logoutShiftId: booking.usersBooking?.[0].logoutShiftId || "",
    officeId: booking.usersBooking?.[0].officeId,
    premiseId: meal?.[0]?.premiseId || mealBooking?.premiseId,
    requestId: mealBooking?.bookingRequestId,
    requestType: mealBooking?.bookingId ? booking.requestType : mode ? mode : "CREATE",
    signinTime: 0,
    startTime: booking.startTime,
    type: "MEALS",
    bookingRequestType: "OFFICE"
  };
  booking.usersBooking.push(mealReq);
  return booking;
}
